import { CLAIM_LINE_LABEL, type ClaimLineHow } from "../../../server/rules/cms1500";
import type { ScreenContext } from "../../shell/types";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { chip, table } from "../../ui/record";
import { openSend } from "../../ui/send";
import { uploadControl } from "../../ui/upload";
import { area, refuse } from "../clients/forms";

// The CMS-1500 (brief 1E-2, mockup v54):
// - the client's "CMS-1500 setup" card (homecare client record): the four steps with "Ready to
//   make claims" or "Not ready", 1 Sample form, 2 Fixed boxes, 3 Service codes, 4 Where every box
//   comes from, and Preview a claim with the most recent patient. Without a setup: the "Claim form"
//   card with Set up CMS-1500. Brief 1E-14 (mockup v64, words v70): Billing provider (boxes 25, 33,
//   33a) and Rendering provider (24J), each with its EIN and NPI under it; one line instead with one
//   business. Brief 1E-15 (mockup v65): step 1 is an accepted claim, optional, for reference; a
//   client billed by invoice shows no claim words;
// - the invoice's "CMS-1500" card: each patient's claim (lines, forms, held or not) with Review
//   CMS-1500 and the client's setup;
// - the "CMS-1500 review" sheet: the form drawn from the same values that print, its holds as
//   chips naming the box, the money check, Form 1 and Form 2, a box clicked to correct it with a
//   reason (logged), Approve and make the PDF, then Print in color, Mail, Fax, Email as secure
//   link and Download, each through the Send sheet with the billing packet.

interface Setup {
  client: { id: string; name: string };
  /** Brief 1E-15: a client billed by invoice shows no claim words at all. */
  billsBy: "invoice" | "invoice_cms1500";
  contract: { id: string; number: string | null } | null;
  templates: { id: string; name: string }[];
  canEdit: boolean;
  setup: null | {
    id: string;
    fixed: {
      carrier: string[];
      box1: string;
      plan: string;
      box23: boolean;
      signature: string;
      prefix: string;
      account: string;
      instructions: boolean;
    };
    lineRules: { group: "date" | "visit" | "day"; perForm: number };
    packet: { kind: string; docType?: string; label: string }[];
    delivery: string;
    sample: { id: string; name: string } | null;
    steps: { label: string; done: boolean }[];
    ready: boolean;
    /** Brief 1E-16: the bill rate card's lines with their claim codes. */
    codes: {
      serviceId: string | null;
      unitId: string;
      service: string;
      hcpcs: string;
      modifiers: string;
      pos: string;
      unit: string;
      rate: string;
      npi: "rendering" | "billing";
      renderingBusinessId: string | null;
      claimLine: ClaimLineHow;
      /** Brief 1E-24: a line of the next version, and a starter code to check. */
      from: string | null;
      suggested: boolean;
    }[];
    codeVersions: { inUse: number | null; next: { number: number; from: string } | null } | null;
    /** Brief 1E-13: mileage on claims, with the payer's code. */
    mileage: { on: boolean; code: string };
    boxes: { box: string; title: string; from: string; kind: string }[];
    /** Brief 1E-14: the live businesses, who bills, who gives the care, and the setup's picks. */
    businesses: Business[];
    billing: Business | null;
    giving: Business | null;
    picks: { billing: string | null; giving: string | null };
    preview: { invoiceId: string; patientId: string; patient: string } | null;
  };
}

interface Business {
  id: string;
  name: string;
  ein: string | null;
  npi: string | null;
}

type Code = NonNullable<Setup["setup"]>["codes"][number];

const npiWords = (b: Business | null) =>
  b?.npi ? `NPI ${b.npi}` : "No NPI yet: add it on its business profile";
const businessWords = (b: Business | null) => `EIN ${b?.ein ?? "–"} · ${npiWords(b)}`;

interface Hold {
  box: string;
  where: string;
  forms: string;
  text: string;
  link: { href: string | null; words: string };
}
export interface ClaimView {
  preview: boolean;
  invoice: { id: string; number: string; status: string; period: string };
  client: { id: string; name: string };
  patient: { id: string; name: string };
  status: "draft" | "approved" | "not made";
  ready: boolean;
  approved: { at: string; by: string } | null;
  file: { id: string; name: string } | null;
  formCount: number;
  lineCount: number;
  total: string;
  invoiceTotal: string;
  forms: {
    index: number;
    suffix: string;
    values: Record<string, string>;
    filled: Record<string, string>;
  }[];
  editable: string[];
  states: Record<string, string[]>;
  locked: Record<string, string>;
  repeats: string[];
  box23: boolean;
  holds: Hold[];
  checks: Hold[];
  money: { ok: boolean; message: string };
  edits: {
    form: number;
    box: string;
    field: string;
    from: string;
    to: string;
    by: string;
    at: string;
    reason: string;
  }[];
  packetParts: string[];
  delivery: string;
  mailTo: string;
  can: { edit: boolean; approve: boolean; send: boolean; markSent: boolean };
}

const KIND_WORDS: Record<string, [string, string]> = {
  fixed: ["Fixed", "plain"],
  patient: ["Each patient", "info"],
  visits: ["Each visit", "info"],
  invoice: ["Each invoice", "info"],
  business: ["Business", "plain"],
};
const BOX1 = ["Medicare", "Medicaid", "Tricare", "Champva", "Group", "Feca", "Other"];

const field = (id: string, label: string, control: HTMLElement, hint?: string) =>
  el(
    "div",
    { class: "field" },
    el("label", { for: id }, label),
    control,
    hint ? el("span", { class: "hint" }, hint) : null,
  );
const section = (
  n: number,
  title: string,
  line: Node | string | null,
  body: Node[],
  open = false,
) =>
  el(
    "details",
    { class: "asec", open },
    el(
      "summary",
      {},
      el("span", { class: "sn" }, String(n)),
      el("b", {}, title),
      line ? (typeof line === "string" ? el("span", { class: "small muted" }, line) : line) : null,
    ),
    el("div", { class: "asb" }, ...body),
  );
const kv = (rows: [string, Node | string][]) =>
  el("dl", { class: "kv" }, ...rows.flatMap(([k, v]) => [el("dt", {}, k), el("dd", {}, v)]));

// ---- The client's setup card ------------------------------------------------------------------------

/** The client's CMS-1500 setup card (or its "Claim form" card while it has none). */
export async function claimSetupCard(host: HTMLElement, clientId: string, ctx: ScreenContext) {
  const res = await api<Setup>("GET", `/api/clients/${encodeURIComponent(clientId)}/claim-setup`);
  if (!res.ok) return;
  // Service codes stays open after a change of mileage on claims, so its code can be typed.
  let codesOpen = false;
  draw(res.body);

  async function save(
    body: Record<string, unknown>,
    done: string,
    note: HTMLElement,
    sub?: string,
  ) {
    codesOpen = body.mileage !== undefined || body.lines !== undefined;
    const r = await api<Setup>(
      "PUT",
      `/api/clients/${encodeURIComponent(clientId)}/claim-setup`,
      body,
    );
    if (!r.ok) {
      note.hidden = false;
      note.textContent = r.body.error ?? "That wasn't saved.";
      return;
    }
    toast(done, sub);
    draw(r.body);
  }

  function draw(s: Setup) {
    if (s.billsBy === "invoice") {
      host.replaceChildren();
      return;
    }
    if (!s.setup) {
      const start = el(
        "button",
        { type: "button", class: "btn sm" },
        icon("plus"),
        " Set up CMS-1500",
      );
      start.addEventListener("click", () => startModal(s));
      host.replaceChildren(
        el(
          "section",
          { class: "card", "aria-label": "Claim form" },
          el(
            "div",
            { class: "cardhead" },
            el(
              "div",
              {},
              el("h2", {}, icon("file"), " Claim form"),
              el(
                "span",
                { class: "small muted" },
                "Bills by invoice and CMS-1500. Set up the CMS-1500 before making claims.",
              ),
            ),
            s.canEdit && s.templates.length ? start : null,
          ),
        ),
      );
      return;
    }
    const st = s.setup;
    const note = el("p", { class: "note", role: "alert", hidden: true });

    // 1 Sample form
    const up = uploadControl({
      kind: "client",
      id: clientId,
      label: st.sample ? "Replace" : "Upload",
      icon: "upload",
      onUploaded: (f) => save({ sampleFileId: f.id }, "Accepted claim uploaded", note),
    });
    // Brief 1E-15 (mockup v65): optional, kept for reference; the setup is ready without it.
    const sample = section(
      1,
      "An accepted claim from this payer",
      el(
        "span",
        {},
        el("span", { class: "small muted" }, "optional, for reference"),
        st.sample ? " " : null,
        st.sample ? chip("Uploaded", "ok") : null,
      ),
      [
        el(
          "div",
          { class: "prow2" },
          el("span", {}, icon("file"), ` ${st.sample?.name ?? "None uploaded"}`),
          s.canEdit ? up.el : null,
        ),
      ],
    );

    // 2 Fixed boxes
    const f = st.fixed;
    const carrier = [0, 1, 2, 3].map((i) =>
      el("input", {
        id: `cs-carrier${i}`,
        type: "text",
        value: f.carrier[i] ?? "",
        "aria-label": `Carrier block line ${i + 1}`,
      }),
    );
    const box1 = el(
      "select",
      { id: "cs-box1" },
      ...BOX1.map((b) => el("option", { value: b, selected: b === f.box1 }, b)),
    );
    const plan = el("input", { id: "cs-plan", type: "text", value: f.plan });
    const sig = el("input", { id: "cs-sig", type: "text", value: f.signature });
    const prefix = el("input", { id: "cs-prefix", type: "text", value: f.prefix });
    const box23 = el("input", { id: "cs-box23", type: "checkbox", checked: f.box23 });
    const instr = el("input", { id: "cs-instr", type: "checkbox", checked: f.instructions });
    const saveFixed = el("button", { type: "button", class: "btn sm pri" }, "Save fixed boxes");
    saveFixed.addEventListener("click", () =>
      save(
        {
          fixed: {
            carrier: carrier.map((c) => c.value),
            box1: box1.value,
            plan: plan.value,
            signature: sig.value,
            prefix: prefix.value,
            box23: box23.checked,
            instructions: instr.checked,
          },
        },
        "Fixed boxes saved",
        note,
      ),
    );
    const fixedBody: Node[] = s.canEdit
      ? [
          el(
            "div",
            { class: "form", style: "max-width:none" },
            el(
              "div",
              { class: "field" },
              el("span", { class: "label" }, "Carrier block (top right)"),
              ...carrier,
            ),
            field("cs-box1", "Box 1", box1),
            field("cs-plan", "Box 11c plan name", plan),
            field("cs-sig", "Boxes 12, 13, 31", sig),
            field(
              "cs-prefix",
              "Invoice prefix (box 26)",
              prefix,
              `${prefix.value || "PREFIX"}-WE + week-ending date`,
            ),
            el(
              "div",
              { class: "checks" },
              el("label", {}, box23, " Box 23: the authorization certificate number"),
              el(
                "label",
                {},
                instr,
                " Print the instructions page after each form, so duplex lines up",
              ),
            ),
            kv([["Boxes 25, 33, 33a", "The billing provider above"]]),
            el("div", { class: "actions" }, saveFixed),
          ),
        ]
      : [
          kv([
            ["Carrier block (top right)", f.carrier.filter(Boolean).join(" · ")],
            ["Box 1", f.box1],
            ["Box 11c plan name", f.plan],
            ["Boxes 12, 13, 31", f.signature],
            [
              "Box 23",
              f.box23
                ? "Authorization certificate number"
                : "Off · the authorization letter goes in the packet",
            ],
            ["Boxes 25, 33, 33a", "The billing provider above"],
            ["Invoice prefix (box 26)", `${f.prefix || "–"}-WE + week-ending date`],
            ["Print instructions page", f.instructions ? "On, so duplex lines up" : "Off"],
          ]),
        ];
    const fixed = section(2, "Fixed boxes", "same on every claim for this client", fixedBody);

    // 3 Service codes (brief 1E-16, mockup v68): the lines of the client's bill rate card, each with
    // its HCPCS, modifiers, rendering provider (24J) and whether it goes on the claim. Prices come
    // from the bill rate card.
    const inputs = new Map<
      Code,
      {
        hcpcs: HTMLInputElement;
        mods: HTMLInputElement;
        by: HTMLSelectElement | null;
        on: HTMLSelectElement;
      }
    >();
    for (const r of st.codes) {
      const by =
        st.businesses.length > 1
          ? el(
              "select",
              { "aria-label": `Rendering provider for ${r.service}`, disabled: !s.canEdit },
              ...st.businesses.map((b) =>
                el("option", { value: b.id, selected: b.id === r.renderingBusinessId }, b.name),
              ),
            )
          : null;
      inputs.set(r, {
        hcpcs: el("input", {
          type: "text",
          value: r.hcpcs,
          "aria-label": `HCPCS for ${r.service}`,
          style: "width:90px",
          disabled: !s.canEdit,
        }),
        mods: el("input", {
          type: "text",
          value: r.modifiers,
          "aria-label": `Modifiers for ${r.service}`,
          style: "width:70px",
          disabled: !s.canEdit,
        }),
        by,
        // Brief 1E-16 addition: its own line, rolled into the trip's line, or not on the claim.
        on: el(
          "select",
          { "aria-label": `${r.service} on the claim`, disabled: !s.canEdit },
          ...(Object.entries(CLAIM_LINE_LABEL) as [ClaimLineHow, string][]).map(([v, label]) =>
            el("option", { value: v, selected: v === r.claimLine }, label),
          ),
        ),
      });
    }
    const codesTable = table(
      [
        {
          h: "Line",
          f: (r: Code) =>
            el(
              "span",
              {},
              el("b", {}, r.service),
              el("div", { class: "small muted" }, `${r.unit} · ${r.rate}`),
              r.from ? el("div", { class: "small muted" }, r.from) : null,
            ),
        },
        {
          h: "HCPCS (24D)",
          f: (r) =>
            el(
              "span",
              {},
              inputs.get(r)?.hcpcs ?? "",
              r.suggested
                ? el("span", { class: "chip warn", style: "margin-left:4px" }, "Suggested")
                : null,
            ),
        },
        { h: "Modifiers", f: (r) => inputs.get(r)?.mods ?? "" },
        {
          h: "Rendering provider (24J)",
          f: (r) =>
            inputs.get(r)?.by ??
            st.businesses.find((b) => b.id === r.renderingBusinessId)?.name ??
            st.billing?.name ??
            "–",
        },
        { h: "On the claim", f: (r) => inputs.get(r)?.on ?? "" },
      ],
      st.codes,
      undefined,
      "Service codes",
    );
    const saveCodes = el("button", { type: "button", class: "btn sm pri" }, "Save service codes");
    saveCodes.addEventListener("click", () =>
      save(
        {
          lines: [...inputs].map(([r, x]) => ({
            serviceId: r.serviceId,
            unitId: r.unitId,
            hcpcs: x.hcpcs.value,
            modifiers: x.mods.value,
            renderingBusinessId: x.by?.value ?? r.renderingBusinessId,
            claimLine: x.on.value,
          })),
        },
        "Service codes saved",
        note,
      ),
    );
    const codes = section(
      3,
      "Service codes",
      st.codeVersions?.next
        ? el(
            "span",
            {},
            el("span", { class: "small muted" }, "what fills box 24 for each service"),
            el(
              "span",
              { class: "chip info", style: "margin-left:6px" },
              `Version ${st.codeVersions.inUse ?? 1} in use · version ${st.codeVersions.next.number} from ${st.codeVersions.next.from} can be coded now`,
            ),
          )
        : "what fills box 24 for each service",
      [
        el(
          "p",
          { class: "small muted", style: "margin:0 0 6px" },
          "The lines of this client's bill rate card. Prices come from the bill rate card.",
        ),
        el(
          "div",
          { class: "codes" },
          st.codes.length
            ? codesTable
            : el(
                "p",
                { class: "muted small" },
                "No bill rate card on the contract yet: open Bill rate card on the client's contract.",
              ),
        ),
        st.codes.some((r) => r.suggested)
          ? el(
              "p",
              { class: "small muted", style: "margin:6px 0 0" },
              "Suggested codes are starters. Check them against a claim the payer accepted.",
            )
          : null,
        s.canEdit && st.codes.length ? el("div", { class: "actions" }, saveCodes) : null,
      ].filter((x): x is NonNullable<typeof x> => !!x),
      codesOpen,
    );

    // Who bills and who gives the care (brief 1E-14, mockup v64): two picks, or one line when the
    // company has one business.
    function businessPicks(): HTMLElement {
      if (st.businesses.length < 2)
        return el(
          "p",
          { class: "small muted", style: "margin:0 0 12px" },
          st.billing
            ? `Billing and rendering provider: ${st.billing.name} · ${businessWords(st.billing)}`
            : "No business yet: add one in Settings › Business profiles.",
        );
      const billing = el(
        "select",
        { id: "cs-billing", disabled: !s.canEdit },
        ...st.businesses.map((x) =>
          el("option", { value: x.id, selected: x.id === st.billing?.id }, x.name),
        ),
      );
      const giving = el(
        "select",
        { id: "cs-giving", disabled: !s.canEdit },
        el("option", { value: "same", selected: !st.picks.giving }, "Same as billing"),
        ...st.businesses
          .filter((x) => x.id !== st.billing?.id)
          .map((x) => el("option", { value: x.id, selected: x.id === st.picks.giving }, x.name)),
      );
      const done = "New claims use it; approved ones keep theirs";
      billing.addEventListener("change", () =>
        save(
          { billingBusiness: billing.value },
          `Claims bill under ${billing.selectedOptions[0]?.textContent ?? ""}`,
          note,
          done,
        ),
      );
      giving.addEventListener("change", () =>
        save(
          { renderingBusiness: giving.value },
          `Box 24J: ${giving.value === "same" ? "same as billing" : (giving.selectedOptions[0]?.textContent ?? "")}`,
          note,
          done,
        ),
      );
      const pick = (id: string, label: string, boxes: string, input: HTMLElement, hint: string) =>
        el(
          "div",
          { class: "field", style: "margin:0" },
          el("label", { for: id }, label, " ", el("span", { class: "small muted" }, boxes)),
          input,
          el("span", { class: "hint" }, hint),
        );
      return el(
        "div",
        { class: "grid2", style: "gap:10px;margin:0 0 12px" },
        pick(
          "cs-billing",
          "Billing provider",
          "boxes 25, 33, 33a",
          billing,
          businessWords(st.billing),
        ),
        pick("cs-giving", "Rendering provider", "box 24J", giving, businessWords(st.giving)),
      );
    }

    // 4 Where every box comes from
    const boxes = section(4, "Where every box comes from", `${st.boxes.length} boxes`, [
      table(
        [
          { h: "Box", f: (r) => el("b", {}, r.title) },
          { h: "Comes from", f: (r) => r.from },
          { h: "", f: (r) => chip(...(KIND_WORDS[r.kind] ?? ["Fixed", "plain"])) },
        ],
        st.boxes,
        undefined,
        "Where every box comes from",
      ),
      el(
        "p",
        { class: "small muted" },
        "A missing patient value holds that patient's claim and names the box.",
      ),
    ]);

    const preview = el(
      "button",
      { type: "button", class: "btn" },
      icon("eye"),
      ` Preview a claim${st.preview ? ` (${st.preview.patient})` : ""}`,
    );
    preview.disabled = !st.preview;
    preview.addEventListener("click", () => void openClaimReview(ctx, { clientPreview: clientId }));
    host.replaceChildren(
      el(
        "section",
        { class: "card cmsset", "aria-label": "CMS-1500 setup" },
        el(
          "div",
          { class: "cardhead" },
          el(
            "div",
            {},
            el("div", { class: "eyebrow" }, "Set up once for this client"),
            el("h2", {}, icon("file"), " CMS-1500 setup"),
            el("span", { class: "small muted" }, "Finish this before making claims."),
          ),
          st.ready ? chip("Ready to make claims", "ok") : chip("Not ready", "bad"),
        ),
        el(
          "div",
          { class: "psteps", style: "margin-bottom:12px" },
          ...st.steps.map((x, i) =>
            el(
              "span",
              { class: x.done ? "done" : undefined },
              x.done ? icon("check") : `${i + 1}`,
              ` ${x.label}`,
            ),
          ),
        ),
        businessPicks(),
        note,
        sample,
        fixed,
        codes,
        boxes,
        el("div", { class: "actions" }, preview),
      ),
    );
  }

  function startModal(s: Setup) {
    const pick = el(
      "select",
      { id: "cs-tpl" },
      ...s.templates.map((t) => el("option", { value: t.id }, t.name)),
    );
    const go = el("button", { type: "button", class: "btn pri" }, "Start the setup");
    const m = openModal({
      title: "Set up CMS-1500",
      sub: `${s.client.name}: start from a template, then edit it for this client.`,
      body: [el("div", { class: "form" }, field("cs-tpl", "Start from", pick))],
      actions: [go],
    });
    go.addEventListener("click", async () => {
      go.disabled = true;
      const r = await api<Setup>(
        "POST",
        `/api/clients/${encodeURIComponent(clientId)}/claim-setup`,
        {
          templateId: pick.value,
        },
      );
      go.disabled = false;
      if (!r.ok) return refuse(m.note, r.body.error);
      m.close();
      toast("CMS-1500 setup started", "Fill in the fixed boxes next");
      draw(r.body);
    });
  }
}

// ---- The invoice's CMS-1500 card ----------------------------------------------------------------------

interface InvoiceClaims {
  setup: { id: string; clientId: string; ready: boolean } | null;
  made: boolean;
  claims: {
    patientId: string;
    patient: string;
    status: string;
    forms: number;
    lines: number;
    total: string;
    holds: string[];
    approved: { at: string } | null;
    box22: { code: string; original: string } | null;
    voided: Voided | null;
  }[];
}

/** A voided claim of a void invoice: sent or not, and its code 8 resend (brief 1E-5). */
interface Voided {
  account: string;
  reason: string | null;
  sent: boolean;
  resent: {
    at: string;
    by: string;
    reason: string;
    file: { id: string; name: string } | null;
  } | null;
  canResend: boolean;
  /** Brief 1E-13: held while the payer's claim number for box 22 isn't on file. */
  held: { text: string; href: string } | null;
}

/** Sends a voided claim again as a void (box 22 code 8), with a reason. */
function resendVoid(invoiceId: string, patientId: string, v: Voided, done: () => void) {
  const why = area("Why is the claim voided with the payer?", "");
  const go = el(
    "button",
    { type: "button", class: "btn pri" },
    icon("paper-airplane"),
    " Make the void",
  );
  const m = openModal({
    title: "Send as a void (code 8)",
    sub: `Box 22 says 8 with the original claim ${v.account}. The PDF goes in the patient's documents, ready to send.`,
    body: [el("div", { class: "form", style: "max-width:none" }, why.el)],
    actions: [go],
  });
  why.input.focus();
  go.addEventListener("click", async () => {
    go.disabled = true;
    const r = await api(
      "POST",
      `/api/invoices/${encodeURIComponent(invoiceId)}/claims/${encodeURIComponent(patientId)}/void`,
      { reason: why.input.value },
    );
    go.disabled = false;
    if (!r.ok) return refuse(m.note, (r.body as { error?: string }).error);
    m.close();
    toast("Void made", "Box 22 code 8, in the patient's documents");
    done();
  });
}

/** The invoice's CMS-1500 card, for a client billed by claim form. Nothing without a setup. */
export async function invoiceClaimsCard(
  host: HTMLElement,
  invoiceId: string,
  client: { id: string | null; name: string },
  ctx: ScreenContext,
  onChange: () => void,
) {
  const res = await api<InvoiceClaims>(
    "GET",
    `/api/invoices/${encodeURIComponent(invoiceId)}/claims`,
  );
  if (!res.ok || !res.body.setup) return host.replaceChildren();
  const a = res.body;
  const setupBtn = el(
    "button",
    { type: "button", class: "btn" },
    icon("gear"),
    " Client CMS-1500 setup",
  );
  setupBtn.addEventListener("click", () => client.id && ctx.go(`/homecare/clients/${client.id}`));
  const rows = a.claims.map((c) => {
    const review = el(
      "button",
      { type: "button", class: "btn pri" },
      icon("eye"),
      " Review CMS-1500",
    );
    review.disabled = c.status === "not made";
    review.addEventListener(
      "click",
      () => void openClaimReview(ctx, { invoiceId, patientId: c.patientId, onChange }),
    );
    if (c.voided) {
      const send = el(
        "button",
        { type: "button", class: "btn" },
        icon("paper-airplane"),
        " Send as a void (code 8)",
      );
      send.addEventListener("click", () =>
        resendVoid(
          invoiceId,
          c.patientId,
          c.voided as Voided,
          () => void invoiceClaimsCard(host, invoiceId, client, ctx, onChange),
        ),
      );
      return el(
        "div",
        { class: "prow2", "aria-label": `CMS-1500 for ${c.patient}` },
        el(
          "span",
          {},
          el("b", {}, c.patient),
          ` · claim ${c.voided.account} `,
          chip("Voided", "plain"),
          c.voided.resent
            ? el(
                "span",
                { class: "small muted" },
                ` Sent as a void (box 22 code 8) ${c.voided.resent.at} by ${c.voided.resent.by}: ${c.voided.resent.reason}`,
              )
            : "",
        ),
        c.voided.canResend
          ? send
          : c.voided.sent && !c.voided.resent && c.voided.held
            ? el(
                "span",
                {},
                chip(`Held · ${c.voided.held.text}`, "bad"),
                " ",
                el("a", { href: c.voided.held.href }, "Add it on the payment"),
              )
            : "",
      );
    }
    const state = c.approved
      ? chip(`Approved ${c.approved.at}`, "ok")
      : c.holds.length
        ? chip(`Held · ${c.holds[0]?.split(":")[0]}`, "bad")
        : chip(c.status === "not made" ? "Not made" : "Draft", "warn");
    return el(
      "div",
      { class: "prow2", "aria-label": `CMS-1500 for ${c.patient}` },
      el(
        "span",
        {},
        el("b", {}, c.patient),
        ` · ${c.lines} line${c.lines === 1 ? "" : "s"} on ${c.forms} form${c.forms === 1 ? "" : "s"} · ${c.total} `,
        state,
        c.box22
          ? chip(
              `Corrected claim · box 22 code ${c.box22.code}${c.box22.original ? ` · replaces ${c.box22.original}` : ""}`,
              "info",
            )
          : "",
      ),
      review,
    );
  });
  const line = !a.made
    ? "Claims are made once the invoice is approved."
    : !a.setup?.ready
      ? `${client.name}'s CMS-1500 setup isn't ready: finish it to make claims.`
      : `Made from this invoice and each patient's record using ${client.name}'s setup · one claim per patient`;
  host.replaceChildren(
    el(
      "section",
      { class: "card cmsset", "aria-label": "CMS-1500" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("h2", {}, icon("file"), " CMS-1500"),
          el("span", { class: "small muted" }, line),
        ),
      ),
      ...rows,
      el("div", { class: "actions" }, setupBtn),
    ),
  );
}

// ---- The review sheet ------------------------------------------------------------------------------------

type Target =
  | { invoiceId: string; patientId: string; form?: number; onChange?: () => void }
  | { clientPreview: string };

/** Opens the CMS-1500 review sheet for a patient's claim (or the client's preview). */
export async function openClaimReview(ctx: ScreenContext, t: Target): Promise<void> {
  const path =
    "clientPreview" in t
      ? `/api/clients/${encodeURIComponent(t.clientPreview)}/claim-setup/preview`
      : `/api/invoices/${encodeURIComponent(t.invoiceId)}/claims/${encodeURIComponent(t.patientId)}`;
  const res = await api<ClaimView>("GET", path);
  if (!res.ok) return toast("The claim couldn't be opened", res.body.error);
  reviewSheet(ctx, res.body, t, "form" in t ? (t.form ?? 1) : 1);
}

const CHECK_WORDS: Record<string, string> = {
  Medicare: "MEDICARE",
  Medicaid: "MEDICAID",
  Tricare: "TRICARE",
  Champva: "CHAMPVA",
  Group: "GROUP",
  Feca: "FECA",
  Other: "OTHER",
};

function reviewSheet(ctx: ScreenContext, v: ClaimView, t: Target, at: number) {
  let form = Math.min(Math.max(1, at), v.formCount);
  const again = (n = form) =>
    "clientPreview" in t
      ? void openClaimReview(ctx, t)
      : void openClaimReview(ctx, { ...t, form: n });

  const chips = el(
    "div",
    { class: "linkrow" },
    ...(v.holds.length
      ? v.holds.map((h) => chip(`${h.text} · ${h.where.toLowerCase()}`, "bad"))
      : [chip("No holds: every required box has a value", "ok")]),
    ...(v.box23 ? [] : [chip("Box 23 off for this client", "plain")]),
    chip(
      v.money.ok ? "Total check: claim = invoice" : "Total check: claim ≠ invoice",
      v.money.ok ? "ok" : "bad",
    ),
  );
  const tabs =
    v.formCount > 1
      ? el(
          "div",
          { class: "seg2", role: "group", "aria-label": "Forms" },
          ...v.forms.map((f) => {
            const b = el(
              "button",
              { type: "button", "aria-pressed": String(f.index === form) },
              `Form ${f.index}`,
            );
            b.addEventListener("click", () => {
              form = f.index;
              paint();
            });
            return b;
          }),
        )
      : null;
  const wrap = el("div", { class: "cmswrap" });
  function paint() {
    for (const b of tabs?.querySelectorAll("button") ?? [])
      b.setAttribute("aria-pressed", String(b.textContent === `Form ${form}`));
    wrap.replaceChildren(drawForm(v, form, (field) => boxClicked(field)));
  }

  function boxClicked(fieldName: string) {
    if (v.locked[fieldName]) return toast(v.locked[fieldName] ?? "", "It can't be typed over");
    if (!v.can.edit)
      return toast(
        v.approved
          ? "This claim is approved and locked"
          : "Corrections are made on a claim of an approved invoice",
      );
    m.close();
    editBox(ctx, v, t, form, fieldName, () => again(form));
  }

  const holdsList = v.holds.length
    ? el(
        "div",
        { class: "note warnnote", role: "status" },
        el("b", {}, "Held: "),
        ...v.holds.flatMap((h, i) => [
          i ? "; " : "",
          `${h.where} (${h.forms.toLowerCase()}): ${h.text}. `,
          h.link.href
            ? (() => {
                const a = el("a", { href: h.link.href }, h.link.words);
                a.addEventListener("click", (e) => {
                  e.preventDefault();
                  m.close();
                  ctx.go(h.link.href as string);
                });
                return a;
              })()
            : el("span", {}, h.link.words),
        ]),
      )
    : null;
  const checks = v.checks.length
    ? el(
        "p",
        { class: "small muted", style: "margin:0" },
        `To look at: ${v.checks.map((c) => `${c.where} ${c.text}`).join("; ")}.`,
      )
    : null;
  const log = v.edits.length
    ? el(
        "div",
        {},
        el("h3", { class: "small" }, "Corrections"),
        table(
          [
            { h: "Form", f: (e) => String(e.form) },
            { h: "Box", f: (e) => el("b", {}, e.box) },
            { h: "Was", f: (e) => e.from || "blank" },
            { h: "Now", f: (e) => e.to || "blank" },
            { h: "Who", f: (e) => e.by },
            { h: "Why", f: (e) => e.reason },
          ],
          v.edits,
          undefined,
          "Corrections",
        ),
      )
    : null;

  // Actions: Approve, then the ways it goes out (each with the billing packet).
  const actions: Node[] = [];
  if (v.approved) actions.push(chip(`Approved ${v.approved.at}`, "ok"));
  else if (!v.preview) {
    const approve = el(
      "button",
      { type: "button", class: "btn pri" },
      icon("check"),
      " Approve and make the PDF",
    );
    approve.disabled = !v.can.approve;
    approve.addEventListener("click", async () => {
      if (!("invoiceId" in t)) return;
      approve.disabled = true;
      const r = await api<ClaimView>(
        "POST",
        `/api/invoices/${encodeURIComponent(t.invoiceId)}/claims/${encodeURIComponent(t.patientId)}/approve`,
      );
      if (!r.ok) {
        approve.disabled = false;
        return refuse(m.note, r.body.error);
      }
      m.close();
      toast("CMS-1500 approved", `PDF saved with the invoice and in ${v.patient.name}'s documents`);
      t.onChange?.();
      reviewSheet(ctx, r.body, t, form);
    });
    actions.push(approve);
  }
  const out = (label: string, ic: string, tab: "Print" | "Mail" | "Fax" | "Email" | "Download") => {
    const b = el(
      "button",
      { type: "button", class: tab === "Download" ? "btn ghost" : "btn" },
      icon(ic),
      ` ${label}`,
    );
    b.disabled = !v.can.send;
    if (!v.can.send)
      b.setAttribute("data-tip", "Approve the claim first: only the approved PDF goes out");
    b.addEventListener("click", async () => {
      if (!("invoiceId" in t)) return;
      b.disabled = true;
      const r = await api<{ file: { id: string; name: string }; document: string; mailTo: string }>(
        "POST",
        `/api/invoices/${encodeURIComponent(t.invoiceId)}/claims/${encodeURIComponent(t.patientId)}/packet`,
      );
      b.disabled = false;
      if (!r.ok) return refuse(m.note, r.body.error);
      m.close();
      void openSend({
        files: [r.body.file],
        document: r.body.document,
        tab,
        mail: { address: r.body.mailTo, color: true },
        onSent: () => again(form),
      });
    });
    return b;
  };
  if (!v.preview)
    actions.push(
      out("Print in color", "file", "Print"),
      out("Mail", "paper-airplane", "Mail"),
      out("Fax", "device-mobile", "Fax"),
      out("Email as secure link", "link", "Email"),
      out("Download", "download", "Download"),
    );
  if (v.can.markSent && "invoiceId" in t) {
    const sent = el(
      "button",
      { type: "button", class: "btn" },
      icon("check"),
      " Mark the invoice sent",
    );
    sent.addEventListener("click", async () => {
      sent.disabled = true;
      const r = await api("POST", `/api/invoices/${encodeURIComponent(t.invoiceId)}/send`, {});
      sent.disabled = false;
      if (!r.ok) return refuse(m.note, r.body.error);
      m.close();
      toast("Marked sent: the due date is set");
      t.onChange?.();
    });
    actions.push(sent);
  }

  const m = openModal({
    title: `${v.patient.name} · ${v.invoice.period}`,
    sub: `CMS-1500 · ${v.invoice.number} · ${v.client.name} · one patient per claim · ${v.lineCount} line${v.lineCount === 1 ? "" : "s"} on ${v.formCount} form${v.formCount === 1 ? "" : "s"} · total ${v.total}${v.money.ok ? " matches the invoice" : ` (the invoice bills ${v.invoiceTotal})`}`,
    wide: true,
    className: "cmsmodal",
    body: [
      v.preview
        ? el(
            "p",
            { class: "note", role: "status" },
            "Preview: the most recent patient's claim from this setup. Nothing is saved.",
          )
        : null,
      chips,
      holdsList,
      tabs ? el("div", {}, tabs) : null,
      wrap,
      el(
        "p",
        { class: "small muted", style: "margin:0" },
        "Click a box to correct it before approving.",
      ),
      checks,
      log,
      v.approved
        ? el(
            "p",
            { class: "small muted", style: "margin:0" },
            `The billing packet: ${v.packetParts.join(", then ")}.`,
          )
        : null,
    ].filter((x): x is NonNullable<typeof x> => !!x),
    actions,
  });
  paint();
}

/** Corrects one box with a reason (logged); on a box every form repeats, on every form if asked. */
function editBox(
  ctx: ScreenContext,
  v: ClaimView,
  t: Target,
  form: number,
  name: string,
  back: () => void,
) {
  if (!("invoiceId" in t)) return back();
  const now = v.forms[form - 1]?.values[name] ?? "";
  const states = v.states[name];
  const input = states
    ? el(
        "select",
        { id: "cb-value" },
        el("option", { value: "" }, "Unticked"),
        ...states.map((s) => el("option", { value: s, selected: s === now }, s)),
      )
    : el("input", { id: "cb-value", type: "text", value: now });
  const reason = el("textarea", { id: "cb-why", rows: "2" });
  const every = el("input", { type: "checkbox", checked: true });
  const repeats = v.formCount > 1 && v.repeats.includes(name);
  const save = el("button", { type: "button", class: "btn pri" }, "Correct the box");
  const cancel = el("button", { type: "button", class: "btn" }, "Cancel");
  const m = openModal({
    title: `Correct ${BOX_LABEL(name)}`,
    sub: `Form ${form} · FlowCare filled "${v.forms[form - 1]?.filled[name] || "blank"}"`,
    body: [
      el(
        "div",
        { class: "form" },
        field("cb-value", "Box value", input),
        field(
          "cb-why",
          "Why is it corrected?",
          reason,
          "Saved in the claim's log with who changed it",
        ),
        repeats ? el("label", {}, every, " Correct it on every form") : null,
      ),
    ],
    actions: [save, cancel],
    onClose: () => undefined,
  });
  cancel.addEventListener("click", () => {
    m.close();
    back();
  });
  save.addEventListener("click", async () => {
    save.disabled = true;
    const r = await api<ClaimView>(
      "POST",
      `/api/invoices/${encodeURIComponent(t.invoiceId)}/claims/${encodeURIComponent(t.patientId)}/edit`,
      {
        form,
        field: name,
        value: input.value,
        reason: reason.value,
        everyForm: repeats && every.checked,
      },
    );
    save.disabled = false;
    if (!r.ok) return refuse(m.note, r.body.error);
    m.close();
    toast("Box corrected", "Logged on the claim");
    t.onChange?.();
    reviewSheet(ctx, r.body, t, form);
  });
}

// ---- The form, drawn from the values that print -----------------------------------------------------------

const LABELS: Record<string, string> = {
  insurance_name: "the carrier's name",
  insurance_address: "the carrier's street",
  insurance_address2: "the carrier's city line",
  insurance_city_state_zip: "the carrier's fourth line",
  insurance_id: "box 1a",
  pt_name: "box 2",
  ins_name: "box 4",
  pt_street: "box 5 street",
  pt_city: "box 5 city",
  pt_state: "box 5 state",
  pt_zip: "box 5 ZIP",
  pt_phone: "box 5 phone",
  ins_street: "box 7 street",
  ins_city: "box 7 city",
  ins_state: "box 7 state",
  ins_zip: "box 7 ZIP",
  ins_plan_name: "box 11c",
  cur_ill_mm: "box 14 month",
  cur_ill_dd: "box 14 day",
  cur_ill_yy: "box 14 year",
  prior_auth: "box 23",
  fac_name: "box 32 name",
  fac_street: "box 32 street",
  fac_location: "box 32 city line",
  doc_name: "box 33 name",
  doc_street: "box 33 street",
  doc_location: "box 33 city line",
};
const BOX_LABEL = (f: string) =>
  LABELS[f] ??
  (/^sv(\d)/.test(f)
    ? `line ${f[2]} date`
    : /^(cpt|mod|ch|day|place|diag)(\d)/.test(f)
      ? `box 24 line ${/\d/.exec(f)?.[0]}`
      : /^diagnosis(\d+)/.test(f)
        ? `box 21 code ${f.slice(9)}`
        : f);

function drawForm(v: ClaimView, n: number, click: (field: string) => void): HTMLElement {
  const f = v.forms[n - 1] as ClaimView["forms"][number];
  const values = f.values;
  const val = (name: string, pad = false) => {
    const text = values[name] ?? "";
    const corrected = (f.filled[name] ?? "") !== text;
    const editable = v.can.edit && v.editable.includes(name);
    const s = el(
      "span",
      {
        class: `cv${text ? "" : " empty"}${corrected ? " fixed" : ""}`,
        "data-field": name,
        role: v.can.edit || v.locked[name] ? "button" : undefined,
        tabindex: v.can.edit || v.locked[name] ? "0" : undefined,
        "aria-label": `${BOX_LABEL(name)}: ${text || "blank"}${corrected ? " (corrected)" : ""}${editable ? "" : v.locked[name] ? " (locked)" : ""}`,
        "data-tip": corrected ? "Corrected: see the log" : undefined,
      },
      text || (pad ? " " : ""),
    );
    if (v.can.edit || v.locked[name]) {
      s.addEventListener("click", () => click(name));
      s.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          click(name);
        }
      });
    }
    return s;
  };
  const x = (name: string, state: string, label: string) => {
    const s = el(
      "span",
      {},
      `${label} `,
      el("span", { class: "cx" }, values[name] === state ? "X" : ""),
    );
    if (v.can.edit && v.editable.includes(name)) {
      s.setAttribute("role", "button");
      s.setAttribute("tabindex", "0");
      s.setAttribute("aria-label", `${label}: ${values[name] === state ? "ticked" : "not ticked"}`);
      s.addEventListener("click", () => click(name));
    }
    return s;
  };
  const box = (num: string, label: string, cls: string | null, ...kids: (Node | string)[]) =>
    el(
      "div",
      { class: `cb${cls ? ` ${cls}` : ""}` },
      el("span", { class: "cl" }, `${num} ${label}`),
      ...kids,
    );
  const br = () => el("br");
  const sp = (s = " ") => document.createTextNode(s);
  const lines = [1, 2, 3, 4, 5, 6].map((k) =>
    el(
      "tr",
      { class: k % 2 ? "sh" : undefined },
      el(
        "td",
        {},
        val(`sv${k}_mm_from`),
        sp(),
        val(`sv${k}_dd_from`),
        sp(),
        val(`sv${k}_yy_from`),
        sp("  "),
        val(`sv${k}_mm_end`),
        sp(),
        val(`sv${k}_dd_end`),
        sp(),
        val(`sv${k}_yy_end`),
      ),
      el("td", {}, val(`place${k}`)),
      el(
        "td",
        {},
        val(`cpt${k}`),
        sp(),
        val(`mod${k}`),
        sp(),
        val(`mod${k}a`),
        sp(),
        val(`mod${k}b`),
        sp(),
        val(`mod${k}c`),
      ),
      el("td", {}, val(`diag${k}`)),
      el("td", { class: "num" }, val(`ch${k}`)),
      el("td", { class: "num" }, val(`day${k}`)),
      el("td", {}, val(`local${k}`)),
    ),
  );
  const dx = Array.from({ length: 12 }, (_, i) => [
    el("small", {}, `${"ABCDEFGHIJKL"[i]}.`),
    sp(),
    val(`diagnosis${i + 1}`),
    sp("  "),
  ]).flat();
  return el(
    "div",
    { class: "cms", "aria-label": `CMS-1500 form ${n} of ${v.formCount}` },
    el(
      "div",
      { class: "cmstop" },
      el("div", { class: "cmsqr" }, "1500"),
      el(
        "div",
        {},
        el("b", {}, "HEALTH INSURANCE CLAIM FORM"),
        el("small", {}, "APPROVED BY NATIONAL UNIFORM CLAIM COMMITTEE (NUCC) 02/12"),
      ),
      el(
        "div",
        { class: "carrier", "aria-label": "Carrier block" },
        val("insurance_name"),
        br(),
        val("insurance_address"),
        br(),
        val("insurance_address2"),
        br(),
        val("insurance_city_state_zip"),
      ),
    ),
    el(
      "div",
      { class: "cr" },
      box(
        "1",
        "",
        "w2",
        el(
          "span",
          { class: "small" },
          ...BOX1.flatMap((b, i) => [i ? " · " : "", x("insurance_type", b, CHECK_WORDS[b] ?? b)]),
        ),
      ),
      box("1a", "INSURED'S I.D. NUMBER", null, val("insurance_id")),
    ),
    el(
      "div",
      { class: "cr" },
      box("2", "PATIENT'S NAME", null, val("pt_name")),
      box(
        "3",
        "BIRTH DATE · SEX",
        null,
        val("birth_mm"),
        sp(),
        val("birth_dd"),
        sp(),
        val("birth_yy"),
        sp(" "),
        x("sex", "M", "M"),
        sp(" "),
        x("sex", "F", "F"),
      ),
      box("4", "INSURED'S NAME", null, val("ins_name")),
    ),
    el(
      "div",
      { class: "cr" },
      box(
        "5",
        "PATIENT'S ADDRESS",
        null,
        val("pt_street"),
        br(),
        val("pt_city"),
        sp("  "),
        val("pt_state"),
        sp("  "),
        val("pt_zip"),
        br(),
        "(",
        val("pt_AreaCode", true),
        ") ",
        val("pt_phone"),
      ),
      box(
        "6",
        "RELATIONSHIP",
        null,
        x("rel_to_ins", "S", "Self"),
        sp(" "),
        x("rel_to_ins", "M", "Spouse"),
        sp(" "),
        x("rel_to_ins", "C", "Child"),
        sp(" "),
        x("rel_to_ins", "O", "Other"),
      ),
      box(
        "7",
        "INSURED'S ADDRESS",
        null,
        val("ins_street"),
        br(),
        val("ins_city"),
        sp("  "),
        val("ins_state"),
        sp("  "),
        val("ins_zip"),
      ),
    ),
    el(
      "div",
      { class: "cr" },
      box(
        "10",
        "CONDITION RELATED TO",
        null,
        "a. Employment ",
        x("employment", "YES", "YES"),
        sp(" "),
        x("employment", "NO", "NO"),
        " · b. Auto ",
        x("pt_auto_accident", "YES", "YES"),
        sp(" "),
        x("pt_auto_accident", "NO", "NO"),
        " · c. Other ",
        x("other_accident", "YES", "YES"),
        sp(" "),
        x("other_accident", "NO", "NO"),
      ),
      box("11c", "PLAN NAME", null, val("ins_plan_name")),
      box(
        "11d",
        "ANOTHER PLAN?",
        null,
        x("ins_benefit_plan", "YES", "YES"),
        sp(" "),
        x("ins_benefit_plan", "NO", "NO"),
      ),
    ),
    el(
      "div",
      { class: "cr" },
      box("12", "PATIENT'S SIGNATURE", null, val("pt_signature")),
      box("13", "INSURED'S SIGNATURE", null, val("ins_signature")),
      box(
        "14",
        "DATE OF INJURY",
        null,
        val("cur_ill_mm"),
        sp(),
        val("cur_ill_dd"),
        sp(),
        val("cur_ill_yy"),
        sp(" "),
        el("small", {}, "QUAL "),
        val("73"),
      ),
    ),
    el(
      "div",
      { class: "cr" },
      box("20", "OUTSIDE LAB?", null, x("lab", "YES", "YES"), sp(" "), x("lab", "NO", "NO")),
      box("21", "DIAGNOSIS · ICD Ind.", "w2", val("99icd"), el("br"), ...dx),
      box("23", "PRIOR AUTHORIZATION", null, val("prior_auth")),
    ),
    el(
      "table",
      { class: "cmsl" },
      el(
        "thead",
        {},
        el(
          "tr",
          {},
          ...[
            "24A DATE(S) OF SERVICE",
            "B POS",
            "D CPT/HCPCS · MOD",
            "E PTR",
            "F CHARGES",
            "G UNITS",
            "J RENDERING NPI",
          ].map((h) => el("th", { scope: "col" }, h)),
        ),
      ),
      el("tbody", {}, ...lines),
    ),
    el(
      "div",
      { class: "cr" },
      box(
        "25",
        "FEDERAL TAX I.D.",
        null,
        val("tax_id"),
        sp(" "),
        x("ssn", "SSN", "SSN"),
        sp(" "),
        x("ssn", "EIN", "EIN"),
      ),
      box("26", "PATIENT'S ACCOUNT NO.", null, val("pt_account")),
      box(
        "27",
        "ACCEPT ASSIGNMENT?",
        null,
        x("assignment", "YES", "YES"),
        sp(" "),
        x("assignment", "NO", "NO"),
      ),
      box("28", "TOTAL CHARGE", null, val("t_charge")),
      box("29", "AMOUNT PAID", null, val("amt_paid")),
    ),
    el(
      "div",
      { class: "cr" },
      box(
        "31",
        "SIGNATURE OF PHYSICIAN OR SUPPLIER",
        null,
        val("physician_signature"),
        br(),
        val("physician_date"),
      ),
      box(
        "32",
        "SERVICE FACILITY",
        null,
        val("fac_name"),
        br(),
        val("fac_street"),
        br(),
        val("fac_location"),
      ),
      box(
        "33",
        "BILLING PROVIDER",
        null,
        val("doc_name"),
        br(),
        val("doc_street"),
        br(),
        val("doc_location"),
        br(),
        "(",
        val("doc_phone area", true),
        ") ",
        val("doc_phone"),
        br(),
        el("small", {}, "33a "),
        val("pin"),
      ),
    ),
    el(
      "div",
      { class: "cmsfoot" },
      `NUCC Instruction Manual available at: www.nucc.org · APPROVED OMB-0938-1197 FORM 1500 (02-12) · Form ${n} of ${v.formCount}`,
    ),
  );
}
